import { useCallback, useMemo } from 'react'

import { UrlSearchParam } from 'consts/url'
import { useSearchParams } from 'router/useSearchParams'
import { Channel } from 'types'

type UpdateParamsOptions = {
  override: boolean
}

type UrlParamValue = string | string[] | null

export const useUrlSearchParams = (
  { replace: replaceInHistory } = { replace: false },
) => {
  const [searchParams, setSearchParams] = useSearchParams({
    [UrlSearchParam.Channel]: Channel.Chat,
  })

  const urlSearchParams = useMemo(
    () =>
      [...searchParams].reduce<Partial<Record<UrlSearchParam, string[]>>>(
        (acc, [key, value]) => {
          acc[key] = value.split(',')

          return acc
        },
        {},
      ),
    [searchParams],
  )

  const updateParams = useCallback(
    (
      data: Partial<Record<UrlSearchParam, UrlParamValue>>,
      { override: fullyOverrideExistingSearchParams }: UpdateParamsOptions = {
        override: false,
      },
    ) => {
      const nextParams = new URLSearchParams(
        fullyOverrideExistingSearchParams ? undefined : searchParams,
      )

      Object.entries(data).forEach(([key, value]) => {
        nextParams.delete(key)

        if (Array.isArray(value)) {
          if (value.length) {
            nextParams.set(key, value.join(','))
          }
        } else if (value) {
          nextParams.set(key, value)
        }
      })

      setSearchParams(nextParams, { replace: replaceInHistory })
    },
    [replaceInHistory, searchParams, setSearchParams],
  )

  const updateParam = useCallback(
    (key: UrlSearchParam, value: UrlParamValue, options?: UpdateParamsOptions) => {
      updateParams({ [key]: value }, options)
    },
    [updateParams],
  )

  const deleteParam = useCallback(
    (key: UrlSearchParam, options?: UpdateParamsOptions) => {
      updateParams({ [key]: null }, options)
    },
    [updateParams],
  )

  const deleteParams = useCallback(
    (keys: UrlSearchParam[], options?: UpdateParamsOptions) => {
      updateParams(
        keys.reduce((acc, key) => {
          acc[key] = null
          return acc
        }, {}),
        options,
      )
    },
    [updateParams],
  )

  return useMemo(
    () => ({
      urlSearchParams,
      setUrlSearchParams: updateParams,
      setUrlSearchParam: updateParam,
      deleteUrlSearchParam: deleteParam,
      deleteUrlSearchParams: deleteParams,
    }),
    [urlSearchParams, updateParam, updateParams, deleteParam, deleteParams],
  )
}
